/**
 * Builds a merged structural tree from JSON for JXPath-style completion.
 * Arrays collapse to a single merged element shape (union of all elements).
 */

const PLACEHOLDER_RE = /^\$\{[^}]+\}$/;

function emptyLeaf() {
  return { kind: 'leaf', leafType: 'unknown', children: {} };
}

/**
 * Merge two shape nodes (union keys for objects, merged element shapes for arrays).
 */
function mergeShapes(a, b) {
  if (!a) return b || emptyLeaf();
  if (!b) return a;

  if (a.kind === 'object' && b.kind === 'object') {
    const keys = new Set([...Object.keys(a.children), ...Object.keys(b.children)]);
    const children = {};
    keys.forEach((k) => {
      children[k] = mergeShapes(a.children[k], b.children[k]);
    });
    return { kind: 'object', children };
  }

  if (a.kind === 'array' && b.kind === 'array') {
    return {
      kind: 'array',
      elementShape: mergeShapes(a.elementShape, b.elementShape),
    };
  }

  if (a.kind === 'array') {
    return {
      kind: 'array',
      elementShape: mergeShapes(a.elementShape, b),
    };
  }
  if (b.kind === 'array') {
    return {
      kind: 'array',
      elementShape: mergeShapes(a, b.elementShape),
    };
  }

  if (a.kind === 'leaf' && b.kind === 'leaf') {
    return {
      kind: 'leaf',
      leafType: a.leafType === b.leafType ? a.leafType : 'mixed',
      children: {},
    };
  }

  if (a.kind === 'object') return a;
  if (b.kind === 'object') return b;

  return {
    kind: 'leaf',
    leafType: 'mixed',
    children: {},
  };
}

function buildNode(value, visited) {
  if (value === null || value === undefined) {
    return { kind: 'leaf', leafType: 'null', children: {} };
  }

  const t = typeof value;

  if (t === 'string') {
    return {
      kind: 'leaf',
      leafType: PLACEHOLDER_RE.test(value.trim()) ? 'placeholder' : 'string',
      children: {},
    };
  }
  if (t === 'number') {
    return { kind: 'leaf', leafType: 'number', children: {} };
  }
  if (t === 'boolean') {
    return { kind: 'leaf', leafType: 'boolean', children: {} };
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return {
        kind: 'array',
        elementShape: emptyLeaf(),
      };
    }
    let merged = buildNode(value[0], visited);
    for (let i = 1; i < value.length; i++) {
      merged = mergeShapes(merged, buildNode(value[i], visited));
    }
    return {
      kind: 'array',
      elementShape: merged,
    };
  }

  if (t === 'object') {
    if (visited.has(value)) {
      return { kind: 'leaf', leafType: 'cyclic-ref', children: {} };
    }
    visited.add(value);
    const children = {};
    Object.keys(value).forEach((key) => {
      children[key] = buildNode(value[key], visited);
    });
    visited.delete(value);
    return { kind: 'object', children };
  }

  return { kind: 'leaf', leafType: 'unknown', children: {} };
}

/**
 * @param {unknown} json - Typically contract data_mapping object
 * @returns {{ kind: string, children?: Object, elementShape?: *, leafType?: string }}
 */
export function buildPathTree(json) {
  if (json === null || json === undefined) {
    return emptyLeaf();
  }
  const visited = new WeakSet();
  return buildNode(json, visited);
}

/**
 * @param {*} tree - Root from buildPathTree
 * @param {{ name: string }[]} steps - Predicate text ignored; only step names used
 * @returns {*} Node at path or null
 */
export function walkPath(tree, steps) {
  let cur = tree;
  for (let i = 0; i < steps.length; i++) {
    const { name } = steps[i];
    if (!cur) return null;

    if (cur.kind === 'array') {
      cur = cur.elementShape;
    }
    if (!cur) return null;

    if (cur.kind === 'object') {
      cur = cur.children[name];
    } else {
      return null;
    }
  }
  return cur;
}

/**
 * Human-readable detail for Monaco completion items.
 */
export function describeNode(node) {
  if (!node) return '';
  if (node.kind === 'object') return 'object';
  if (node.kind === 'array') return 'array';
  if (node.kind === 'leaf') return node.leafType || 'value';
  return '';
}
